import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import { useToasts } from "react-toast-notifications";
import {
  MDBBadge,
  MDBBtn,
  MDBCard,
  MDBCardBody,
  MDBIcon,
  MDBTable,
} from "mdbreact";
import { GenericProducts } from "../../../../constants";
import { axioKit } from "../../../../services/utilities";
import RequestModal from "../../laboratory/purchaseOrders/requestModal";
import { PURCHASE_URL } from "../../laboratory/purchaseOrders/common";
import {
  breakdownOf,
  levelsOf,
  packSizeOf,
  packUnitOf,
  baseOf,
} from "../../laboratory/catalog/products/packagesEditor";

const PRODUCTS_URL = "commerce/merchandise/products";
const keyOf = (productId, variant) => `${productId}:${variant || ""}`;

const STATUS = {
  out: { label: "Out of stock", color: "danger", icon: "times-circle" },
  low: { label: "Low stock", color: "warning", icon: "exclamation-triangle" },
  inStock: { label: "In stock", color: "success", icon: "check-circle" },
};

const FILTERS = [
  { key: "", label: "All" },
  { key: "reorder", label: "Needs reorder" },
  { key: "out", label: "Out of stock" },
  { key: "low", label: "Low stock" },
  { key: "inStock", label: "In stock" },
];

// the cart survives a refresh, it is only a convenience
const cartKey = (branch) => `supplyHubCart_${branch}`;
const readCart = (branch) => {
  try {
    return JSON.parse(localStorage.getItem(cartKey(branch))) || {};
  } catch {
    return {};
  }
};
const writeCart = (branch, cart) => {
  try {
    localStorage.setItem(cartKey(branch), JSON.stringify(cart));
  } catch {
    // storage may be blocked, the cart then lives until the page closes
  }
};

/**
 * Supply Hub > Browse Supplies: every registered supply of the branch with its
 * stock, so the frontdesk sees what runs low and requests it from procurement.
 * The request is a Purchase Order, approved by the Sr. MLS / Chief MLS.
 */
export default function BrowseSupplies() {
  const { token, activePlatform } = useSelector(({ auth }) => auth),
    branch = activePlatform?.branchId,
    [products, setProducts] = useState([]),
    [shelf, setShelf] = useState({}),
    [stock, setStock] = useState({}),
    [onOrder, setOnOrder] = useState({}),
    [isLoading, setIsLoading] = useState(false),
    [isSaving, setIsSaving] = useState(false),
    [search, setSearch] = useState(""),
    [category, setCategory] = useState(""),
    [filter, setFilter] = useState(""),
    [cart, setCart] = useState({}),
    [showRequest, setShowRequest] = useState(false),
    { addToast } = useToasts(),
    history = useHistory();

  useEffect(() => {
    if (branch) setCart(readCart(branch));
  }, [branch]);

  const updateCart = (next) => {
    setCart(next);
    writeCart(branch, next);
  };

  const load = useCallback(() => {
    if (!branch || !token) return;
    setIsLoading(true);
    Promise.all([
      axioKit.universal(`${PRODUCTS_URL}/browse`, token, { branch }),
      axioKit.universal(`${PURCHASE_URL}/shelf`, token, { branch }),
      axioKit.universal(`${PURCHASE_URL}/stock`, token, { branch }),
      axioKit.universal(`${PURCHASE_URL}/browse`, token, {
        branch,
        status: "pending,approved",
      }),
    ])
      .then(([_products, _shelf, _stock, _orders]) => {
        setProducts(_products?.payload || []);
        const byKey = (list = []) =>
          list.reduce((result, entry) => {
            result[keyOf(entry.product, entry.variant)] = entry;
            return result;
          }, {});
        setShelf(byKey(_shelf?.payload));
        setStock(byKey(_stock?.payload));
        // what is already requested or approved but not yet received, in base units
        const ordered = {};
        (_orders?.payload || []).forEach(({ status, items = [] }) =>
          items.forEach(({ productId, variant, qnty = {}, unitSize = 1 }) => {
            const qty = status === "approved" ? qnty.approved : qnty.request;
            if (!(qty > 0)) return;
            const key = keyOf(productId?._id || productId, variant);
            ordered[key] = (ordered[key] || 0) + qty * (unitSize || 1);
          }),
        );
        setOnOrder(ordered);
      })
      .catch((error) =>
        addToast(error.message, { appearance: "error", autoDismiss: true }),
      )
      .finally(() => setIsLoading(false));
  }, [branch, token, addToast]);

  useEffect(() => {
    load();
  }, [load]);

  // one row per brand + variant of a known generic
  const rows = useMemo(
    () =>
      products
        .filter(({ pgid }) => GenericProducts.find(pgid))
        .flatMap((product) => {
          const generic = GenericProducts.find(product.pgid);
          const variants = product.variants?.length
            ? product.variants
            : [{ _id: "", name: "" }];
          return variants.map((variant) => {
            const key = keyOf(product._id, variant._id);
            const levels = levelsOf(variant, product);
            const packQty = Math.max(packSizeOf(variant, product), 1);
            const onHand = Math.max(
              stock[key]?.onHand ?? shelf[key]?.onHand ?? 0,
              0,
            );
            // the reorder level is in its unit (box, pack, pcs...), the largest by default
            const reorderAt =
              levels.find(({ bu }) => bu === variant.reorderUnit) || levels[0];
            const hasReorder =
              variant.reorderLevel !== undefined && variant.reorderLevel !== null;
            const reorderBase =
              (Number(variant.reorderLevel) || 0) *
              (reorderAt?.qnty || packQty);
            const status =
              onHand <= 0
                ? "out"
                : hasReorder && onHand <= reorderBase
                  ? "low"
                  : "inStock";
            return {
              key,
              productId: product._id,
              variant: variant._id || "",
              generic,
              brand: product.name,
              variantName: variant.name,
              base: baseOf(variant, product),
              unit: packUnitOf(variant, product),
              packQty,
              levels,
              onHand,
              expired: shelf[key]?.expired || 0,
              sealed: shelf[key]?.sealed || 0,
              reorder: hasReorder
                ? `${variant.reorderLevel} ${reorderAt?.bu || ""}`.trim()
                : "",
              ordered: onOrder[key] || 0,
              status,
            };
          });
        })
        // what needs attention first, then by name
        .sort(
          (a, b) =>
            ["out", "low", "inStock"].indexOf(a.status) -
              ["out", "low", "inStock"].indexOf(b.status) ||
            a.generic.name.localeCompare(b.generic.name) ||
            a.brand.localeCompare(b.brand),
        ),
    [products, stock, shelf, onOrder],
  );

  const categories = useMemo(
    () => [...new Set(rows.map(({ generic }) => generic.category))].sort(),
    [rows],
  );

  const counts = useMemo(() => {
    const result = { "": rows.length, reorder: 0, out: 0, low: 0, inStock: 0 };
    rows.forEach(({ status }) => {
      result[status] += 1;
      if (status !== "inStock") result.reorder += 1;
    });
    return result;
  }, [rows]);

  const visible = useMemo(() => {
    const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return rows.filter((row) => {
      if (category && row.generic.category !== category) return false;
      if (filter === "reorder" && row.status === "inStock") return false;
      if (filter && filter !== "reorder" && row.status !== filter) return false;
      const text = [
        row.generic.abbreviation,
        row.generic.name,
        row.brand,
        row.variantName,
      ]
        .join(" ")
        .toLowerCase();
      return words.every((word) => text.includes(word));
    });
  }, [rows, search, category, filter]);

  const cartLines = Object.values(cart);

  const toggleCart = (row) => {
    const next = { ...cart };
    if (next[row.key]) delete next[row.key];
    else
      next[row.key] = { productId: row.productId, variant: row.variant, qty: 1 };
    updateCart(next);
  };

  const handleRequest = async (data) => {
    setIsSaving(true);
    try {
      const result = await axioKit.save(
        PURCHASE_URL,
        { ...data, branchId: branch },
        token,
      );
      addToast(result?.success || "Request submitted.", {
        appearance: "success",
        autoDismiss: true,
      });
      setShowRequest(false);
      updateCart({});
      load();
    } catch (error) {
      addToast(error.message, { appearance: "error", autoDismiss: true });
    } finally {
      setIsSaving(false);
    }
  };

  const units = ({ onHand, levels, base }) =>
    breakdownOf(onHand, levels) || `${onHand.toLocaleString()} ${base}`;

  return (
    <MDBCard narrow className="pb-3">
      <MDBCardBody>
        {/* header */}
        <div
          className="d-flex flex-wrap justify-content-between align-items-center mb-3"
          style={{ gap: 8 }}
        >
          <div>
            <h5 className="mb-0">
              <MDBIcon icon="boxes" className="mr-2 text-primary" />
              Browse Supplies
            </h5>
            <small className="text-muted">
              Pick what runs low and send a request to procurement (approved by
              the Sr. MLS / Chief MLS).
            </small>
          </div>
          <div className="d-flex align-items-center" style={{ gap: 8 }}>
            <MDBBtn
              size="sm"
              color="light"
              className="m-0"
              onClick={() =>
                history.push(
                  history.location.pathname.replace(
                    /\/browse\/?$/,
                    "/requests/submitted",
                  ),
                )
              }
            >
              <MDBIcon icon="tasks" className="mr-1" /> My requests
            </MDBBtn>
            <MDBBtn
              size="sm"
              color="primary"
              className="m-0"
              disabled={!cartLines.length}
              onClick={() => setShowRequest(true)}
            >
              <MDBIcon icon="shopping-cart" className="mr-1" />
              Request
              <MDBBadge color="light" className="ml-2 text-dark" pill>
                {cartLines.length}
              </MDBBadge>
            </MDBBtn>
          </div>
        </div>

        {/* filters */}
        <div
          className="d-flex flex-wrap justify-content-between align-items-center mb-3"
          style={{ gap: 8 }}
        >
          <div className="btn-group flex-wrap" role="group" aria-label="Stock">
            {FILTERS.map(({ key, label }) => (
              <button
                key={key || "all"}
                type="button"
                className={`btn btn-sm m-0 z-depth-0 ${
                  filter === key ? "btn-primary" : "btn-outline-primary"
                }`}
                style={{ textTransform: "none" }}
                onClick={() => setFilter(key)}
              >
                {label}
                <MDBBadge
                  color={filter === key ? "light" : "primary"}
                  className={`ml-2 ${filter === key ? "text-dark" : ""}`}
                >
                  {counts[key] || 0}
                </MDBBadge>
              </button>
            ))}
          </div>
          <div className="d-flex align-items-center" style={{ gap: 8 }}>
            <select
              className="browser-default custom-select custom-select-sm"
              style={{ width: 170 }}
              value={category}
              onChange={({ target }) => setCategory(target.value)}
              aria-label="Category"
            >
              <option value="">All categories</option>
              {categories.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
            <div className="position-relative" style={{ width: 260 }}>
              <MDBIcon
                icon="search"
                className="position-absolute text-muted"
                style={{ top: "50%", left: 10, transform: "translateY(-50%)" }}
              />
              <input
                className="form-control form-control-sm"
                style={{ paddingLeft: 30 }}
                placeholder="Search generic, brand or variant"
                value={search}
                onChange={({ target }) => setSearch(target.value)}
              />
            </div>
          </div>
        </div>

        {/* list */}
        <MDBTable responsive hover small className="mb-0">
          <thead className="grey lighten-4">
            <tr>
              <th>Supply</th>
              <th>Category</th>
              <th className="text-right">On hand</th>
              <th>Status</th>
              <th className="text-right">On order</th>
              <th className="text-center" style={{ width: 120 }}>
                Request
              </th>
            </tr>
          </thead>
          <tbody>
            {!visible.length && (
              <tr>
                <td colSpan={6} className="text-center text-muted py-5">
                  {isLoading
                    ? "Loading supplies…"
                    : !rows.length
                      ? "No supply registered yet. The laboratory registers brands in Products."
                      : "No supply matches your filters."}
                </td>
              </tr>
            )}
            {visible.map((row) => {
              const status = STATUS[row.status];
              const inCart = !!cart[row.key];
              return (
                <tr key={row.key} className={inCart ? "blue lighten-5" : ""}>
                  <td className="align-middle">
                    <code className="mr-2">{row.generic.abbreviation}</code>
                    <span className="font-weight-bold">{row.generic.name}</span>
                    <small className="d-block text-muted">
                      {[row.brand, row.variantName].filter(Boolean).join(" · ")}
                      {row.packQty > 1 &&
                        ` · ${row.unit} of ${row.packQty} ${row.base}`}
                    </small>
                  </td>
                  <td className="align-middle small">{row.generic.category}</td>
                  <td className="align-middle text-right">
                    <span className="font-weight-bold">{units(row)}</span>
                    {!!row.expired && (
                      <small className="d-block text-danger">
                        {row.expired.toLocaleString()} {row.base} expired
                      </small>
                    )}
                    {row.reorder && (
                      <small className="d-block text-muted">
                        reorder at {row.reorder}
                      </small>
                    )}
                  </td>
                  <td className="align-middle">
                    <MDBBadge color={status.color}>
                      <MDBIcon icon={status.icon} className="mr-1" />
                      {status.label}
                    </MDBBadge>
                  </td>
                  <td className="align-middle text-right small">
                    {row.ordered ? (
                      <span title="Requested or approved, not yet received">
                        {breakdownOf(row.ordered, row.levels) ||
                          `${row.ordered.toLocaleString()} ${row.base}`}
                      </span>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td className="align-middle text-center py-1">
                    <MDBBtn
                      size="sm"
                      color={inCart ? "success" : "outline-primary"}
                      className="m-0 px-3"
                      onClick={() => toggleCart(row)}
                    >
                      <MDBIcon
                        icon={inCart ? "check" : "cart-plus"}
                        className="mr-1"
                      />
                      {inCart ? "Added" : "Add"}
                    </MDBBtn>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </MDBTable>

        <RequestModal
          show={showRequest}
          toggle={() => setShowRequest(false)}
          products={products}
          stock={stock}
          isLoading={isSaving}
          onSubmit={handleRequest}
          initialLines={cartLines}
        />
      </MDBCardBody>
    </MDBCard>
  );
}
